let tabSetCounter = 0; // global counter for tabsets

export const tabset = {
	init: () => {
		const url = new URL(window.location);
		const searchParams = url.searchParams;

		// Find every .tabset on the page
		const tabsets = document.querySelectorAll('.tabset');
		if (!tabsets.length) return;

		tabsets.forEach((tabset) => {
			// 1) Check if this tabset has a custom ID in the data attribute
			let customId = tabset.dataset.customtabsetid;
			tabSetCounter++;
			const paramName = customId
				? customId
				: `tab-instance${tabSetCounter}`;

			const tabLinks = tabset.querySelectorAll('.tabset__tab-link');
			const tabContents = tabset.querySelectorAll('.tabset__content');
			if (!tabLinks.length || !tabContents.length) return;

			// 2) Setup custom divider images
			tabset.querySelectorAll('.tabset__tab-item').forEach((tab) => {
				const imageUrl = tab.getAttribute('data-divider-image');
				if (imageUrl) {
					tab.style.setProperty('--image-url', `url(${imageUrl})`);
				}
			});

			let currentActiveIndex = -1;
			let isTransitioning = false; // to prevent rapid clicking issues

			// Helper to fade out old content, then fade in new
			function switchTab(newIndex) {
				// If user clicks the same tab or if we’re mid‐transition, do nothing
				if (newIndex === currentActiveIndex || isTransitioning) return;

				isTransitioning = true;
				const oldIndex = currentActiveIndex;
				currentActiveIndex = newIndex;

				// Update tab link active state
				if (oldIndex >= 0 && tabLinks[oldIndex]) {
					tabLinks[oldIndex].classList.remove('active');
				}
				tabLinks[newIndex]?.classList.add('active');

				// If no old tab (first load), just fade in the new content
				if (oldIndex === -1) {
					fadeIn(newIndex);
					return;
				}

				// Otherwise, fade out the old content, then fade in the new
				fadeOut(oldIndex, () => {
					fadeIn(newIndex);
				});
			}

			function fadeOut(index, callback) {
				const content = tabContents[index];
				if (!content) {
					callback?.();
					return;
				}
				// Ensure it’s visible while fading
				content.classList.remove('fade-in');
				content.classList.add('fade-out');

				// Wait for CSS transition to complete
				const duration = parseFloat(getComputedStyle(content).transitionDuration);
				const timeout = duration * 1000;

				setTimeout(() => {
					// After fade-out, hide entirely
					content.classList.remove('fade-out');
					content.style.display = 'none';
					callback?.();
				}, timeout);
			}

			function fadeIn(index) {
				const content = tabContents[index];
				if (!content) {
					isTransitioning = false;
					return;
				}
				// Display block so we can fade in
				content.style.display = 'block';
				content.classList.add('fade-in');

				// Wait for fade-in to finish
				const duration = parseFloat(getComputedStyle(content).transitionDuration);
				const timeout = duration * 1000;

				setTimeout(() => {
					isTransitioning = false;
				}, timeout);
			}

			// Function to set the URL parameter with the correct tab identifier (custom ID or index)
			function setTabParam(index) {
				// If there's a custom ID in the link, use it; otherwise use the numeric index
				const customTabId = tabLinks[index]?.dataset?.customtabid;
				searchParams.set(paramName, customTabId ? customTabId : index);
				url.search = searchParams.toString();
				window.history.replaceState({}, '', url.toString());
			}

			// 3) On initial load, see if there's a saved tab in the URL
			let savedParamValue = searchParams.get(paramName);
			let initialIndex;

			if (savedParamValue != null) {
				// First, try to find a tab link that has a matching data-customtabid
				const foundIndex = Array.from(tabLinks).findIndex(
					(link) => link.dataset.customtabid === savedParamValue
				);
				if (foundIndex >= 0) {
					initialIndex = foundIndex;
				} else {
					// Otherwise, see if the saved param is a valid numeric index
					const numericIndex = parseInt(savedParamValue, 10);
					if (
						!isNaN(numericIndex) &&
						numericIndex >= 0 &&
						numericIndex < tabLinks.length
					) {
						initialIndex = numericIndex;
					}
				}
			}

			// If we still don't have an initialIndex, default to 0
			if (typeof initialIndex === 'undefined') {
				initialIndex = 0;
				setTabParam(initialIndex);
			}

			// Switch to that tab
			switchTab(initialIndex);

			// 4) Handle clicks
			tabLinks.forEach((link, i) => {
				link.addEventListener('click', (e) => {
					e.preventDefault();
					switchTab(i);
					// Update the URL param
					setTabParam(i);
				});
			});
		});
	},
};

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
	tabset.init();
});
